// HeaderV3 — sticky top premium-dark header with mega-menu + user widget.
//
// Structure (desktop):
//   [Logo]  [MegaMenu · Prezzi]   ...   [Widget|CTAs]
// Mobile:
//   [Logo]  ...  [Widget|CTAs]  [Hamburger]
//   Hamburger opens a full-screen drawer with the mega-menu structure.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LogoV3 from "./LogoV3";
import MegaMenuV3 from "./MegaMenu/MegaMenuV3";
import { MENU_GROUPS } from "./MegaMenu/menuData";
import CTAGroupV3 from "./CTAGroupV3";
import MiniDashboardWidget from "./UserWidget/MiniDashboardWidget";
import PrimaryNavV3 from "./PrimaryNav/PrimaryNavV3";
import LanguageSwitcher from "@/components/LanguageSwitcher";
// Phase II · Wave D.2 · additive · Wave-D LocaleSwitcher wire-in.
// Rendered alongside the legacy M14 ``LanguageSwitcher``:
//   · M14 switch → changes M14 i18n (15 locales, cookie ``glms_lang``)
//   · Wave D switch → changes Wave D i18n (40 locales, cookie
//     ``glms_locale``) + ALSO dual-writes ``glms_lang`` for compat
// so a user clicking the new switcher never desyncs the two layers.
// Only the Wave D switcher is user-visible going forward; the legacy
// M14 one stays mounted for existing E2E tests until Wave D.3.
import LocaleSwitcher from "@/components/i18n/LocaleSwitcher";
// Phase II · Wave E.1a · Currency switcher sibling of LocaleSwitcher.
import CurrencySwitcher from "@/components/currency/CurrencySwitcher";

export default function HeaderV3() {
    const { t } = useTranslation("header_v3");
    const { user } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (!mobileOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [mobileOpen]);

    const isLogged = !!user && user !== false;

    return (
        <>
            <header
                data-testid="header-v3"
                className="sticky top-0 z-50 border-b transition-colors backdrop-blur"
                style={{
                    backgroundColor: scrolled ? "rgba(11, 15, 25, 0.85)" : "#0A0A0A",
                    borderColor: "#1A1A1A",
                    WebkitBackdropFilter: "blur(10px)",
                    backdropFilter: "blur(10px)",
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 lg:h-[68px] flex items-center justify-between gap-4">
                    <div className="flex items-center gap-8">
                        <LogoV3 />
                        {isLogged ? <PrimaryNavV3 /> : <MegaMenuV3 />}
                    </div>
                    <div className="flex items-center gap-2">
                        {isLogged ? (
                            <MiniDashboardWidget />
                        ) : (
                            <>
                                <div
                                    className="hidden sm:block"
                                    data-testid="header-v3-lang-switcher-wrap"
                                >
                                    <LanguageSwitcher />
                                </div>
                                <div
                                    className="hidden sm:block"
                                    data-testid="header-locale-switcher"
                                >
                                    <LocaleSwitcher />
                                </div>
                                <div
                                    className="hidden sm:block"
                                    data-testid="header-currency-switcher"
                                >
                                    <CurrencySwitcher compact />
                                </div>
                                <CTAGroupV3 />
                            </>
                        )}
                        <button
                            type="button"
                            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/90 hover:text-white hover:bg-white/[0.06]"
                            onClick={() => setMobileOpen(true)}
                            aria-label={t("mobile.open_menu")}
                            data-testid="header-v3-hamburger"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[60] lg:hidden"
                        data-testid="header-v3-mobile-drawer"
                    >
                        <div
                            className="absolute inset-0"
                            style={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
                            onClick={() => setMobileOpen(false)}
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute right-0 top-0 bottom-0 w-full max-w-sm overflow-y-auto p-6"
                            style={{ backgroundColor: "#0A0A0A", borderLeft: "1px solid #1A1A1A" }}
                        >
                            <div className="flex items-center justify-between mb-8">
                                <LogoV3 testId="header-v3-mobile-logo" />
                                <button
                                    type="button"
                                    onClick={() => setMobileOpen(false)}
                                    aria-label={t("mobile.close_menu")}
                                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white/90 hover:bg-white/[0.06]"
                                    data-testid="header-v3-mobile-close"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Groups as expanded lists */}
                            <div className="space-y-6">
                                {MENU_GROUPS.map((group) => (
                                    <div key={group.key}>
                                        <div className="text-[10px] uppercase tracking-widest text-[#999999] font-semibold mb-2">
                                            {t(group.i18n)}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            {group.items.map((item) => {
                                                const isExternal = item.href.startsWith("mailto:") || item.href.startsWith("http");
                                                const cls = "block px-2 py-2 rounded-lg text-sm text-white/90 hover:text-white hover:bg-white/[0.04] no-underline";
                                                return isExternal ? (
                                                    <a key={item.key} href={item.href} className={cls} onClick={() => setMobileOpen(false)}>
                                                        {t(item.i18n)}
                                                    </a>
                                                ) : (
                                                    <Link key={item.key} to={item.href} className={cls} onClick={() => setMobileOpen(false)}>
                                                        {t(item.i18n)}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                                <div className="pt-2 border-t border-white/5">
                                    <a
                                        href="/v3#pricing"
                                        className="block px-2 py-2 rounded-lg text-sm text-white font-medium hover:bg-white/[0.04] no-underline"
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        {t("menu.prezzi")}
                                    </a>
                                </div>
                            </div>

                            {/* CTA row / user widget */}
                            <div className="mt-8 pt-6 border-t border-white/5">
                                {isLogged ? (
                                    <MiniDashboardWidget />
                                ) : (
                                    <div className="flex flex-col gap-4">
                                        <div
                                            className="flex items-center justify-between"
                                            data-testid="header-v3-mobile-lang-wrap"
                                        >
                                            <span className="text-xs uppercase tracking-widest text-text-secondary">
                                                {t("mobile.language", { defaultValue: "Language" })}
                                            </span>
                                            <div className="flex flex-col gap-2 items-end">
                                                <LanguageSwitcher />
                                                <div data-testid="header-locale-switcher-mobile">
                                                    <LocaleSwitcher />
                                                </div>
                                                <div data-testid="header-currency-switcher-mobile">
                                                    <CurrencySwitcher compact />
                                                </div>
                                            </div>
                                        </div>
                                        <CTAGroupV3 />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
